import {
  CatalogueItemsController_create_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCircleCheck,
  IconLoader2,
  IconUpload,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';

import { EmptyState, Screen, ScreenFact, ScreenNote, ScreenTable } from '../back-office/Screen';
import { paths } from '../paths';
import { labelForKind } from './catalogue-kinds';
import {
  CATALOGUE_UPLOAD_COLUMNS,
  type CatalogueUploadFile,
  isReady,
  OUTLET_SEPARATOR,
  readCatalogueUpload,
  type ReadyRow,
  type RejectedRow,
  summariseUpload,
  type UploadRow,
} from './catalogue-upload';
import {
  countOutcomes,
  type RowOutcome,
  runCatalogueUpload,
  type UploadRunResult,
} from './catalogue-upload-run';

/**
 * Creating the catalogue from a file: pick, read, review, create, and a result
 * that can be run again.
 *
 * The park has around five hundred things it sells and creates them one at a
 * time in a form. This is the screen that is not that.
 *
 * ## Four steps, and the third is the one that matters
 *
 * `pick -> read -> review -> create`, and **nothing leaves the browser until the
 * review is accepted**. `code` and `kind` are immutable once set and a code is
 * never reissued (FR-215), so a typo that reaches the API is permanent: the item
 * can be retired, but it cannot be corrected and its code cannot be used again.
 * Five hundred of those on one press is what the review step stands in front of.
 *
 * The reading and the judging are `catalogue-upload.ts`; the creating is
 * `catalogue-upload-run.ts`. Both are here as modules rather than as handlers
 * because both are decisions with consequences a screen cannot demonstrate — the
 * meaning of a 409 in particular — and because a test that has to render a file
 * picker to ask what a refusal means is a test of the wrong thing.
 *
 * ## Already existed is not a failure, and this screen must not read as one
 *
 * `CATALOGUE_CODE_TAKEN` means that row is already created. The result therefore
 * has **three** counts and not two, skipped rows are shown in their own quiet
 * band rather than beside the failures, and the summary sentence for a run with
 * nothing failed says so plainly however many rows were skipped.
 * `412 created, 88 already existed, 0 failed` is a successful re-run of a file
 * whose first attempt stopped part way, and it must look like one.
 *
 * ## No nav entry
 *
 * This is an action on the catalogue, not a destination — it is reached from the
 * Catalogue screen and `NavLink` keeps Catalogue lit while it is open, which is
 * where somebody on it actually is. `requireRole('OWNER')`, mirroring the API:
 * every write on `CatalogueItemsController` inherits the class's
 * `@RequireRole('OWNER')`, and the route says why in as many words — "only the
 * owner stocks the catalogue (CL-206)".
 */

/** Where the screen is. One value, so two steps cannot be true at once. */
type Stage =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'read'; readonly filename: string; readonly file: CatalogueUploadFile }
  | {
      readonly kind: 'creating';
      readonly filename: string;
      readonly rows: readonly ReadyRow[];
      readonly done: readonly RowOutcome[];
    }
  | {
      readonly kind: 'done';
      readonly filename: string;
      readonly rows: readonly ReadyRow[];
      readonly result: UploadRunResult;
    };

export function CatalogueUpload() {
  const client = useApiClient();

  const fileInputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletsRead, setOutletsRead] = useState(false);

  const [stage, setStage] = useState<Stage>({ kind: 'waiting' });

  /** Set by the Stop button and read between rows by the run. */
  const stopping = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: a dozen outlets, and every row of the file is matched against
    // all of them at once. `docs/ui-conventions.md`, "Reading a list", case one.
    collectPages<OutletResource>((from, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) {
          setOutlets(all);
          setOutletsRead(true);
        }
      })
      .catch(() => {
        // Not the screen's failure, and deliberately not fatal. A file naming no
        // outlet at all is a perfectly good file, and every row that does name
        // one is rejected at review with the outlet named back — which is a
        // sentence somebody can act on, rather than a screen that will not open.
        if (live) setOutletsRead(true);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  const readFile = useCallback(
    async (chosen: File) => {
      const text = await chosen.text();

      setStage({ kind: 'read', filename: chosen.name, file: readCatalogueUpload(text, outlets) });
    },
    [outlets],
  );

  const start = useCallback(
    async (rows: readonly ReadyRow[], filename: string) => {
      stopping.current = false;

      const done: RowOutcome[] = [];

      setStage({ kind: 'creating', filename, rows, done: [] });

      const result = await runCatalogueUpload({
        rows,

        // One key per row, minted at the moment that row is sent. A key names one
        // attempt at one piece of work, and each row here is its own piece of
        // work — `POST /v1/catalogue/items` refuses the request outright without
        // one (`SYNC_IDEMPOTENCY_KEY_MISSING`). A whole file re-run mints fresh
        // keys, which is correct: it is a new attempt, and the rows that already
        // landed are protected by `CATALOGUE_CODE_TAKEN` rather than by the key.
        create: (row) =>
          client.request(CatalogueItemsController_create_v1, {
            body: row.body,
            headers: { [IDEMPOTENCY_KEY_HEADER]: newIdempotencyKey() },
          }),

        onOutcome: (outcome) => {
          done.push(outcome);

          // A copy per tick, so the live count re-renders. The array is small —
          // one entry per row attempted — and this is the only place it grows.
          setStage({ kind: 'creating', filename, rows, done: [...done] });
        },

        shouldStop: () => stopping.current,
      });

      setStage({ kind: 'done', filename, rows, result });
    },
    [client],
  );

  const reset = useCallback(() => {
    stopping.current = false;
    setStage({ kind: 'waiting' });

    // The input holds the last file, and a browser fires no `change` for the
    // same file chosen twice. Cleared so that correcting the file and picking it
    // again under the same name is a thing that works.
    if (fileInput.current !== null) fileInput.current.value = '';
  }, []);

  const running = stage.kind === 'creating';

  return (
    <Screen
      title="Create from a file"
      lede="The whole catalogue in one pass"
      description="A file of items, checked row by row and then created one at a time. Nothing is sent until you have seen what it would create. Running the same file again is safe: anything already created is skipped rather than duplicated, because a code is issued once and never reissued."
      action={
        <Button
          variant="outline"
          // `nativeButton={false}` because the rendered element is an anchor.
          // Base UI otherwise warns that native button semantics have been
          // removed, and it is right to: a link claiming to be a button is
          // announced as one and then does not answer the space bar.
          nativeButton={false}
          render={<Link to={paths.catalogue} />}
        >
          <IconArrowLeft data-icon="inline-start" />
          Back to the catalogue
        </Button>
      }
      aside={
        <>
          {stage.kind !== 'waiting' && (
            <ScreenFact label="File">
              <span className="font-mono">{stage.filename}</span>
            </ScreenFact>
          )}

          {stage.kind === 'read' && stage.file.kind === 'read' && <Counts rows={stage.file.rows} />}

          {/* Last in the bar, so the facts before it take the space `Screen`
              gives them and this sits at the field's right edge. */}
          <div className="space-y-2">
            <label
              htmlFor={fileInputId}
              className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase"
            >
              Choose a file
            </label>

            {/* A plain file input. The registry has no control for this, and the
                one thing that must not happen is a styled button opening a
                picker the keyboard cannot reach — `docs/ui-conventions.md` asks
                for a component composed of primitives, and here the primitive is
                the browser's own. */}
            <input
              ref={fileInput}
              id={fileInputId}
              type="file"
              accept=".csv,text/csv,text/plain"
              disabled={running || !outletsRead}
              className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:text-foreground disabled:opacity-60"
              onChange={(event) => {
                const chosen = event.target.files?.[0];

                if (chosen !== undefined) void readFile(chosen);
              }}
            />
          </div>
        </>
      }
    >
      {stage.kind === 'waiting' &&
        (outletsRead ? (
          <div className="space-y-10">
            {/* `EmptyState` is written for the inside of `TableNotice`, which is
                what centres it and caps its measure. There is no table here, so
                that wrapper is supplied rather than the component restyled —
                left to itself at the field's full width it sets as two stranded
                full-bleed lines. */}
            <div className="mx-auto max-w-md space-y-3 py-14 text-center">
              <EmptyState
                title="Choose a file of items to create"
                description="Every row is checked against the catalogue's rules and against your outlets before anything is sent, and you see what would be created before it is."
              />
            </div>

            <WhatAFileLooksLike />
          </div>
        ) : (
          <div aria-hidden className="h-40 animate-pulse rounded-2xl bg-muted" />
        ))}

      {stage.kind === 'read' && stage.file.kind === 'unreadable' && (
        <div className="space-y-10">
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{stage.file.problem}</AlertDescription>
          </Alert>

          <WhatAFileLooksLike />
        </div>
      )}

      {stage.kind === 'read' && stage.file.kind === 'read' && (
        <Review rows={stage.file.rows} onCreate={(rows) => void start(rows, stage.filename)} />
      )}

      {stage.kind === 'creating' && (
        <Running
          total={stage.rows.length}
          done={stage.done}
          onStop={() => {
            stopping.current = true;
          }}
        />
      )}

      {stage.kind === 'done' && (
        <Finished attempted={stage.rows.length} result={stage.result} onAgain={reset} />
      )}
    </Screen>
  );
}

/* -------------------------------------------------------------------------- */
/* What a file is                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The four things somebody needs to know before they fill a file in.
 *
 * **In the field rather than in `Screen`'s `guidance` margin, and that is a
 * correction rather than a preference.** Passing `guidance` tells `Screen` the
 * page is a form and caps its children at a measure — which is right for the
 * five screens that use it and wrong here: the review's tables are the widest
 * thing in the back office, and capped they ran under the margin's own notes.
 * Shot at 1920 and 1280, which is where that showed.
 *
 * So it sits where it is read: in front of somebody who has not chosen a file
 * yet, and in front of somebody whose file would not open. Once there is a file
 * to look at, the review is the thing to look at.
 */
function WhatAFileLooksLike() {
  return (
    <Band name="What a file looks like">
      <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
        <ScreenNote label="The columns">
          <p>
            <span className="font-mono">{CATALOGUE_UPLOAD_COLUMNS.join(', ')}</span>
          </p>
          <p className="mt-1.5">
            The first five must be there. <span className="font-mono">tracksStock</span> is a yes or
            a no and is blank for no; <span className="font-mono">outlets</span> names the outlets
            this sells at, by code or by name, several separated by a{' '}
            <span className="font-mono">{OUTLET_SEPARATOR}</span>. A column this screen does not
            know is ignored, so notes of your own can stay in the file.
          </p>
        </ScreenNote>

        <ScreenNote label="Money is whole paise">
          As the API carries it and as the exports write it: twenty rupees is{' '}
          <span className="font-mono">2000</span>, never <span className="font-mono">20.00</span>{' '}
          and never <span className="font-mono">20</span>. An amount written as rupees is refused at
          the review rather than multiplied by a hundred on a guess.
        </ScreenNote>

        <ScreenNote label="The code and the kind are permanent">
          Neither can be changed after an item exists, and a code is never reissued — not even once
          the item is retired. A row created with a typo can only be retired and created again under
          a different code, so the review is the last moment either can be corrected.
        </ScreenNote>

        <ScreenNote label="Running it again is safe">
          Items are created one at a time, so a run that stops part way leaves everything before it
          complete and sellable. Fix the file and upload it again: the rows already created come
          back as already existing and are skipped.
        </ScreenNote>
      </div>
    </Band>
  );
}

/* -------------------------------------------------------------------------- */
/* The review                                                                  */
/* -------------------------------------------------------------------------- */

/** The standing figures about the file, in the bar beside it. */
function Counts({ rows }: { rows: readonly UploadRow[] }) {
  const summary = summariseUpload(rows);

  return (
    <>
      <ScreenFact label="Will be created">
        <span className="font-mono numeric">{summary.ready}</span>{' '}
        {summary.ready === 1 ? 'row' : 'rows'}
      </ScreenFact>

      {summary.rejected > 0 && (
        <ScreenFact label="Cannot be created">
          <span className="font-mono numeric">{summary.rejected}</span>{' '}
          {summary.rejected === 1 ? 'row' : 'rows'}
        </ScreenFact>
      )}
    </>
  );
}

/**
 * What the file would do, and the button that does it.
 *
 * The two things surfaced here rather than accepted silently are the zero costs
 * and the permanence of a code. Both are decisions the owner is entitled to make
 * and neither is this screen's to refuse — what it can do is make sure they are
 * made knowingly, once, rather than discovered on a profit and loss a month
 * later.
 */
function Review({
  rows,
  onCreate,
}: {
  rows: readonly UploadRow[];
  onCreate: (rows: readonly ReadyRow[]) => void;
}) {
  const summary = summariseUpload(rows);
  const ready = rows.filter(isReady);
  const rejected = rows.filter((row): row is RejectedRow => !row.ready);

  return (
    <div className="space-y-8">
      {summary.zeroCost > 0 && (
        // Not a refusal, and not `destructive`: zero cost is accepted by the API
        // deliberately, and a park that does not track what its souvenirs cost is
        // making a real choice. What it must not be is a silent one.
        <Alert>
          <IconAlertTriangle />
          <AlertDescription>
            <span className="font-mono numeric">{summary.zeroCost}</span>{' '}
            {summary.zeroCost === 1 ? 'row has' : 'rows have'} no cost. Margin will read as 100% for{' '}
            {summary.zeroCost === 1 ? 'that item' : 'those items'} on the profit and loss and on
            item reporting. That is a legitimate answer if the garden does not track what they cost
            — just not an accidental one.
          </AlertDescription>
        </Alert>
      )}

      {rejected.length > 0 && (
        <Band
          name={`${String(rejected.length)} ${rejected.length === 1 ? 'row' : 'rows'} cannot be created`}
        >
          <p className="text-sm text-muted-foreground">
            These are not sent. Correct them in the file and upload it again — the rows below them
            are unaffected either way, and anything created in the meantime is skipped on the next
            run.
          </p>

          {/* `measured` — the default — and not `stated`, which is what a table
              ending in text normally wants. `stated` sizes the table to
              `max-content` and hands the overflow to the records' own scroll,
              which is right for a row of stated columns and wrong for a column
              of sentences: a sentence's max-content is the sentence on one line,
              so at 1280 the reason ran off the field's edge instead of wrapping.
              Automatic layout is what lets `whitespace-normal` do its work. */}
          <ScreenTable label="Rows that cannot be created">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Row</TableHead>
                <TableHead className="w-64">Name</TableHead>
                <TableHead>What is wrong</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rejected.map((row) => (
                <TableRow key={row.rowNumber}>
                  <TableCell className="font-mono text-muted-foreground numeric">
                    {row.rowNumber}
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    {row.name === '' ? (
                      <span className="text-muted-foreground">No name</span>
                    ) : (
                      row.name
                    )}
                  </TableCell>
                  <TableCell className="whitespace-normal text-muted-foreground">
                    <ul className="space-y-1">
                      {row.problems.map((problem) => (
                        <li key={problem}>{problem}</li>
                      ))}
                    </ul>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScreenTable>
        </Band>
      )}

      <Band name={`${String(ready.length)} ${ready.length === 1 ? 'row' : 'rows'} will be created`}>
        {ready.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing in this file can be created as it stands.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Read this before creating them. The code and the kind cannot be changed afterwards and
              a code is never reissued, so a mistake here is permanent.
            </p>

            <ScreenTable label="Rows that will be created">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Row</TableHead>
                  <TableHead className="w-32">Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28">Kind</TableHead>
                  <TableHead>Sold at</TableHead>
                  <TableHead className="text-right">Selling</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ready.map((row) => (
                  <TableRow key={row.rowNumber}>
                    <TableCell className="font-mono text-muted-foreground numeric">
                      {row.rowNumber}
                    </TableCell>
                    <TableCell className="font-mono tracking-wider numeric">{row.code}</TableCell>
                    <TableCell>
                      {row.name}
                      {row.body.tracksStock && (
                        <>
                          {' '}
                          <Badge variant="secondary">Counts stock</Badge>
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {labelForKind(row.body.kind)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.outletNames.length === 0 ? 'Nowhere yet' : row.outletNames.join(', ')}
                    </TableCell>
                    <TableCell className="text-right font-mono numeric">
                      {formatPaise(row.body.sellingPaise)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono numeric ${row.body.costPaise === 0 ? 'text-muted-foreground' : ''}`}
                    >
                      {formatPaise(row.body.costPaise)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScreenTable>

            <div className="mt-8">
              <Button
                onClick={() => {
                  onCreate(ready);
                }}
              >
                <IconUpload data-icon="inline-start" />
                Create {ready.length} {ready.length === 1 ? 'item' : 'items'}
              </Button>
            </div>
          </>
        )}
      </Band>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The run, and what came of it                                                */
/* -------------------------------------------------------------------------- */

/**
 * A live count while the rows are created.
 *
 * `role="status"` and `aria-live="polite"`: this changes five hundred times and
 * announcing each one would be unusable, so the polite region says where it has
 * got to when the reader is between things.
 */
function Running({
  total,
  done,
  onStop,
}: {
  total: number;
  done: readonly RowOutcome[];
  onStop: () => void;
}) {
  const counts = countOutcomes(done);

  return (
    <div className="space-y-6">
      <p role="status" className="flex items-center gap-2 font-medium text-foreground">
        <IconLoader2 className="size-4 animate-spin" aria-hidden />
        Creating item <span className="font-mono numeric">{done.length + 1}</span> of{' '}
        <span className="font-mono numeric">{total}</span>
      </p>

      <Tally counts={counts} />

      <p className="text-sm text-muted-foreground">
        One at a time, in the file&rsquo;s order. Everything created so far is complete and sellable
        — stopping leaves them alone, and running the same file again picks up from where this got
        to.
      </p>

      <Button variant="outline" onClick={onStop}>
        Stop after this one
      </Button>
    </div>
  );
}

/** The result: three counts, the failures by row, and the way to go again. */
function Finished({
  attempted,
  result,
  onAgain,
}: {
  attempted: number;
  result: UploadRunResult;
  onAgain: () => void;
}) {
  const counts = countOutcomes(result.outcomes);
  const failures = result.outcomes.filter(
    (outcome): outcome is Extract<RowOutcome, { kind: 'failed' }> => outcome.kind === 'failed',
  );

  const notAttempted = attempted - result.outcomes.length;

  return (
    <div className="space-y-8">
      {/* The headline. A run with nothing failed is a success however many rows
          were skipped — "412 created, 88 already existed, 0 failed" is a re-run
          of a file that stopped part way, and it must not read as a fault. */}
      <Alert role="status" variant={counts.failed > 0 ? 'destructive' : undefined}>
        {counts.failed > 0 ? <IconAlertTriangle /> : <IconCircleCheck />}
        <AlertDescription>
          {counts.failed === 0
            ? counts.skipped === 0
              ? 'Every item in the file was created.'
              : 'Done, with nothing failed. The rows that already existed were left exactly as they are — a code is issued once and is never reissued, so a second run cannot duplicate one.'
            : 'Some rows were refused. Everything created is complete and sellable; correct the rows below in the file and upload it again.'}
        </AlertDescription>
      </Alert>

      <Tally counts={counts} />

      {result.halted !== null && (
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertDescription>
            This stopped at row{' '}
            <span className="font-mono numeric">{result.halted.at.rowNumber}</span> and did not
            attempt the {notAttempted} {notAttempted === 1 ? 'row' : 'rows'} after it, because the
            failure was not about that row: {messageForFailure(result.halted.error)} Nothing was
            lost — upload the same file again once it is sorted and it will carry on from here.
          </AlertDescription>
        </Alert>
      )}

      {result.stopped && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>
            Stopped, with {notAttempted} {notAttempted === 1 ? 'row' : 'rows'} not attempted.
            Everything created is complete and sellable. Upload the same file again to carry on.
          </AlertDescription>
        </Alert>
      )}

      {failures.length > 0 && (
        <Band name={`${String(failures.length)} ${failures.length === 1 ? 'row' : 'rows'} refused`}>
          <ScreenTable label="Rows the server refused">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Row</TableHead>
                <TableHead className="w-64">Name</TableHead>
                <TableHead>Why</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failures.map((failure) => (
                <TableRow key={failure.row.rowNumber}>
                  <TableCell className="font-mono text-muted-foreground numeric">
                    {failure.row.rowNumber}
                  </TableCell>
                  <TableCell className="whitespace-normal">{failure.row.name}</TableCell>
                  <TableCell className="whitespace-normal text-muted-foreground">
                    {messageForFailure(failure.error)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScreenTable>
        </Band>
      )}

      {counts.skipped > 0 && (
        <Band name={`${String(counts.skipped)} already existed`}>
          <p className="text-sm text-muted-foreground">
            These carry a code the garden has already issued for that kind of item, so they were
            already created and nothing was written for them. That is what makes running the same
            file twice safe — and it is not a failure.
          </p>

          <ScreenTable label="Rows that already existed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Row</TableHead>
                <TableHead className="w-32">Code</TableHead>
                <TableHead>Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.outcomes
                .filter((outcome) => outcome.kind === 'skipped')
                .map((outcome) => (
                  <TableRow key={outcome.row.rowNumber}>
                    <TableCell className="font-mono text-muted-foreground numeric">
                      {outcome.row.rowNumber}
                    </TableCell>
                    <TableCell className="font-mono tracking-wider numeric">
                      {outcome.row.code}
                    </TableCell>
                    <TableCell className="whitespace-normal">{outcome.row.name}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </ScreenTable>
        </Band>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onAgain}>
          Upload another file
        </Button>
        <Button nativeButton={false} render={<Link to={paths.catalogue} />}>
          See the catalogue
        </Button>
      </div>
    </div>
  );
}

/**
 * The three counts, side by side and named.
 *
 * Three and never two. Folding "already existed" into either of the others is
 * the mistake this whole ticket is about: added to the failures it turns a
 * successful re-run into a broken import, and added to the creations it claims
 * this app wrote something it did not.
 */
function Tally({ counts }: { counts: { created: number; skipped: number; failed: number } }) {
  return (
    <dl className="grid grid-cols-3 gap-4">
      <Figure term="Created">{counts.created}</Figure>
      <Figure term="Already existed">{counts.skipped}</Figure>
      <Figure term="Failed">{counts.failed}</Figure>
    </dl>
  );
}

function Figure({ term, children }: { term: string; children: number }) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3">
      <dt className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {term}
      </dt>
      <dd className="mt-1 font-mono text-2xl text-foreground numeric">{children}</dd>
    </div>
  );
}

/**
 * A named band of the page, so each is a landmark a reader can jump to.
 *
 * The same heading-and-hairline composition as `ItemExport`, `ProfitAndLoss`,
 * `DayClose` and `TradingSummary`. That is now five callers and well past the
 * point `docs/ui-conventions.md` asks for a promotion into
 * `back-office/Screen.tsx` — named in the PR body rather than taken here, since
 * the move touches four screens across three epics while their tickets are in
 * flight.
 */
function Band({ name, children }: { name: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId}>
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {name}
        </h2>
      </header>
      <div className="pt-4">{children}</div>
    </section>
  );
}
